import React, { useEffect, useRef, useState } from 'react'
import {
  SITE_CHAT_GREETING,
  clearSiteChatSession,
  loadSiteChatSession,
  saveSiteChatSession,
  siteChatCreateSession,
  siteChatHistory,
  siteChatSend,
} from '../utils/site-chat-api'
import './site-chat.css'

function clock(iso) {
  const d = iso ? new Date(iso) : new Date()
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function mapHistory(rows) {
  return (rows || [])
    .filter((m) => m && m.text)
    .map((m) => ({
      role: m.role === 'user' || m.role === 'CLIENT' ? 'user' : 'agent',
      text: m.text,
      at: clock(m.at),
    }))
}

function isMobileChat() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(max-width: 640px), (hover: none) and (pointer: coarse)').matches
}

export default function SiteChat() {
  const [open, setOpen] = useState(false)
  const [boot, setBoot] = useState('idle')
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const [handedOff, setHandedOff] = useState(false)
  const [unread, setUnread] = useState(0)
  const sessionRef = useRef(loadSiteChatSession())
  const logRef = useRef(null)
  const inputRef = useRef(null)
  const panelRef = useRef(null)
  const openRef = useRef(false)
  const lockedY = useRef(0)
  const viewportDefault = useRef('')

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [messages, open, pending])

  useEffect(() => {
    if (!open) return undefined

    const html = document.documentElement
    const body = document.body
    const meta = document.querySelector('meta[name="viewport"]')
    viewportDefault.current = meta?.getAttribute('content') || 'width=device-width, initial-scale=1.0'

    const clearPanel = () => {
      const panel = panelRef.current
      if (!panel) return
      panel.style.top = ''
      panel.style.height = ''
      panel.style.bottom = ''
      panel.style.transform = ''
    }

    const syncViewport = () => {
      const panel = panelRef.current
      if (!panel || !isMobileChat()) {
        clearPanel()
        return
      }
      const vv = window.visualViewport
      if (!vv) {
        panel.style.top = '0px'
        panel.style.height = '100dvh'
        panel.style.bottom = 'auto'
        return
      }
      panel.style.top = `${Math.max(0, vv.offsetTop)}px`
      panel.style.height = `${Math.max(240, vv.height)}px`
      panel.style.bottom = 'auto'
      panel.style.transform = 'none'
      if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
    }

    const mobile = isMobileChat()
    if (mobile) {
      lockedY.current = window.scrollY || 0
      html.classList.add('site-chat-open')
      body.style.top = `-${lockedY.current}px`
      if (meta) {
        meta.setAttribute(
          'content',
          'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover',
        )
      }
      syncViewport()
    } else if (inputRef.current) {
      inputRef.current.focus()
    }

    const onViewport = () => syncViewport()
    window.visualViewport?.addEventListener('resize', onViewport)
    window.visualViewport?.addEventListener('scroll', onViewport)
    window.addEventListener('resize', onViewport)

    return () => {
      window.visualViewport?.removeEventListener('resize', onViewport)
      window.visualViewport?.removeEventListener('scroll', onViewport)
      window.removeEventListener('resize', onViewport)
      clearPanel()
      if (mobile) {
        html.classList.remove('site-chat-open')
        body.style.position = ''
        body.style.top = ''
        body.style.width = ''
        if (meta) meta.setAttribute('content', viewportDefault.current)
        window.scrollTo(0, lockedY.current)
      }
    }
  }, [open])

  async function getSession() {
    if (sessionRef.current?.clientId && sessionRef.current?.sessionToken) {
      return sessionRef.current
    }
    const created = await siteChatCreateSession()
    sessionRef.current = created
    saveSiteChatSession(created)
    return created
  }

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setUnread(0)
    ;(async () => {
      setBoot('loading')
      setError('')
      try {
        const s = await getSession()
        if (cancelled) return
        let hist = { messages: [] }
        try {
          hist = await siteChatHistory(s)
        } catch (err) {
          if (err.status === 401) {
            clearSiteChatSession()
            sessionRef.current = null
            const fresh = await getSession()
            if (cancelled) return
            hist = await siteChatHistory(fresh).catch(() => ({ messages: [] }))
          } else {
            throw err
          }
        }
        if (cancelled) return
        const mapped = mapHistory(hist.messages)
        setMessages(
          mapped.length
            ? mapped
            : [{ role: 'agent', text: SITE_CHAT_GREETING, at: clock() }],
        )
        setHandedOff(Boolean(hist.handedOff))
        setBoot('ready')
      } catch {
        if (!cancelled) {
          setBoot('error')
          setError('Chat is temporarily unavailable. Request an SMS or book a demo instead.')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open])

  async function onSend(e) {
    e?.preventDefault()
    const text = draft.trim()
    if (!text || pending || handedOff) return
    setDraft('')
    setPending(true)
    setError('')
    setMessages((m) => [...m, { role: 'user', text, at: clock() }])
    try {
      const s = await getSession()
      const r = await siteChatSend(text, s)
      const reply = (r.reply || '').trim()
      if (r.handedOff) setHandedOff(true)
      if (reply) {
        setMessages((m) => [...m, { role: 'agent', text: reply, at: clock() }])
        if (!openRef.current) setUnread((n) => n + 1)
      }
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: 'agent',
          text: 'I could not send that just now. Try again, or leave a number via SMS on this page.',
          at: clock(),
        },
      ])
    } finally {
      setPending(false)
    }
  }

  return (
    <div className={`sc${open ? ' is-open' : ''}`}>
      {open ? (
        <div className="sc-panel" role="dialog" aria-label="Chat with ADEL" ref={panelRef}>
          <header className="sc-head">
            <div className="sc-avatar" aria-hidden="true">
              A
            </div>
            <div>
              <strong>ADEL</strong>
              <span>Sarah · usually replies in a minute</span>
            </div>
            <button type="button" className="sc-x" onClick={() => setOpen(false)} aria-label="Close chat">
              ×
            </button>
          </header>
          <div className="sc-log" ref={logRef}>
            {boot === 'loading' ? <p className="sc-status">Connecting…</p> : null}
            {boot === 'error' ? <p className="sc-status is-err">{error}</p> : null}
            {messages.map((m, i) => (
              <div key={`${m.role}-${i}-${m.at}`} className={`sc-bubble is-${m.role}`}>
                <p>{m.text}</p>
                {m.at ? <time>{m.at}</time> : null}
              </div>
            ))}
            {pending ? (
              <div className="sc-bubble is-agent is-typing">
                <span />
                <span />
                <span />
              </div>
            ) : null}
          </div>
          {handedOff ? (
            <p className="sc-handoff">Continue on SMS — we just texted you.</p>
          ) : (
            <form className="sc-form" onSubmit={onSend}>
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write a message…"
                maxLength={4000}
                disabled={boot !== 'ready' || pending}
                aria-label="Message"
                enterKeyHint="send"
                autoComplete="off"
              />
              <button type="submit" disabled={boot !== 'ready' || pending || !draft.trim()} aria-label="Send">
                Send
              </button>
            </form>
          )}
        </div>
      ) : null}
      {open ? null : (
        <button
          type="button"
          className="sc-fab"
          onClick={() => setOpen(true)}
          aria-label="Open chat"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v7A2.5 2.5 0 0 1 16.5 16H9l-4 3.2V6.5Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
          {unread > 0 ? <i>{unread > 9 ? '9+' : unread}</i> : null}
        </button>
      )}
    </div>
  )
}
